/**
 * Login Components
 * A form component handling user authentication and role-based access
 * Features email/password authentication with redirect handling and role verification
 */

import FormContainer from "../../utils/form-container";
import { useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import useAuth from "../../hooks/use-auth";
import useAuthHook from "../admin/api/auth";
import { inputEmail, inputPassword } from "../../utils/dynamic-form";
import useUserRolesHook from "./api/user-roles";

/**
 * Login Component
 * Main authentication component providing login functionality and role-based access control
 *
 * Features:
 * - Email and password authentication
 * - User role verification
 * - Redirect handling based on authentication state
 * - Local storage management for user data
 * - Form validation and error handling
 *
 * @returns {JSX.Element} A login form with authentication controls
 */
const Login = () => {
  const { auth, setAuth } = useAuth();

  let [searchParams] = useSearchParams();
  const pathName = searchParams.get("next") || "/admin";

  const navigate = useNavigate();

  /**
   * Form handling configuration using react-hook-form
   * Manages form state and validation
   */
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const { postLogin } = useAuthHook();
  const { isLoading, mutateAsync, isSuccess, data } = postLogin;

  /**
   * User role hook configuration
   * Fetches and manages user role data
   */
  const { postUserRoleById } = useUserRolesHook({
    page: 1,
    q: "",
    limit: 10000000,
  });

  /**
   * Handles form submission and authentication
   * @param {Object} data - Form data containing email and password
   */
  const submitHandler = async (data) => {
    mutateAsync(data);
  };

  /**
   * User role mutation configuration
   * Manages user role verification and storage
   */
  const { mutateAsync: userRoleMutateAsync, data: userRole } = postUserRoleById;

  /**
   * Effect hook to handle successful authentication
   * Manages user role verification and navigation
   */
  useEffect(() => {
    if (isSuccess) {
      userRoleMutateAsync(data._id);
      if (userRole) {
        localStorage.setItem("userRole", JSON.stringify(userRole));
        localStorage.setItem("userInfo", JSON.stringify(data));

        setAuth({
          userInfo: data,
          userRole: userRole,
        });
        navigate(pathName);
      }
    }
  }, [isSuccess, userRole]);

  /**
   * Effect hook to handle authenticated users
   * Redirects authenticated users to home page
   */
  useEffect(() => {
    auth?.userInfo && navigate("/");
  }, [navigate]);

  // Style per correggere l'autofill
  const autofillStyle = `
    input:-webkit-autofill,
    input:-webkit-autofill:hover, 
    input:-webkit-autofill:focus,
    input:-webkit-autofill:active {
      -webkit-box-shadow: 0 0 0 30px white inset !important;
      -webkit-text-fill-color: black !important;
    }
  `;

  return (
    <div className="h-screen w-full flex items-center justify-center bg-white">
      <style>{autofillStyle}</style>
      <div className="p-6 shadow-md md:w-full max-w-2xl bg-white rounded-2xl border border-gray-200">
        <h2 className="w-full text-start text-3xl font-bold mb-4 text-gray-800">
          Sign In
        </h2>
        <form
          onSubmit={handleSubmit(submitHandler)}
          className="grid grid-cols-1 md:grid-cols-2 gap-4 text-lg"
        >
          {inputEmail({
            register,
            errors,
            label: "Email",
            name: "email",
            placeholder: "Email",
            autoComplete: "current-username",
          })}
          {inputPassword({
            register,
            errors,
            label: "Password",
            name: "password",
            placeholder: "Password",
            autoComplete: "current-password",
          })}
          <div className="col-span-1 md:col-span-2">
            <button
              type="submit"
              className="w-full bg-red-600 hover:bg-red-700 text-white font-bold py-2 px-4 rounded-md focus:outline-none focus:ring-2"
              style={{ backgroundColor: "#dc2626" }}
              disabled={isLoading}
            >
              {isLoading ? (
                <span className="spinner-border spinner-border-sm" />
              ) : (
                "Sign In"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;
